import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMarketStatus } from './useMarketStatus.ts';

const AIFloatingWidget: React.FC = () => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [newsPanelOpen, setNewsPanelOpen] = useState(false);

    const { isOpen: isMarketOpen, status, holidayReason } = useMarketStatus();

    useEffect(() => {
        const handler = (event: Event) => setNewsPanelOpen(Boolean((event as CustomEvent).detail?.open));
        window.addEventListener('tad-news-panel', handler);
        return () => window.removeEventListener('tad-news-panel', handler);
    }, []);

    useEffect(() => {
        if (!open) return;
        const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    if (newsPanelOpen) return null;

    return (
        <div className={`fixed bottom-6 left-6 ${open ? 'z-[62]' : 'z-50'} flex flex-col items-start rtl:items-end gap-3`}>

            {/* Inline styles for custom sleek animations */}
            <style>
                {`
          @keyframes slideUpFade {
            from { opacity: 0; transform: translateY(16px) scale(0.96); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes pulse-live {
            0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
            70% { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
            100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
          }
          .pulse-dot {
            animation: pulse-live 2s infinite;
          }
          .glass-panel {
            background: rgba(15, 23, 42, 0.95);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            border: 1px solid rgba(139, 92, 246, 0.3);
          }
        `}
            </style>

            {open && (
                <div
                    className="glass-panel rounded-2xl shadow-2xl shadow-purple-900/20 p-5 w-72 sm:w-80 relative overflow-hidden group"
                    style={{ animation: 'slideUpFade 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards', transformOrigin: 'bottom left' }}
                >

                    {/* Glow Effects */}
                    <div className="absolute -top-12 -right-12 w-32 h-32 bg-purple-600/20 rounded-full blur-2xl transition-all duration-700 group-hover:bg-purple-600/30"></div>
                    <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-blue-600/20 rounded-full blur-xl transition-all duration-700 group-hover:bg-blue-600/30"></div>

                    {/* Close Button */}
                    <button
                        onClick={() => setOpen(false)}
                        className="absolute top-3 left-3 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 p-1.5 rounded-full z-10"
                        aria-label="إغلاق"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>

                    {/* Header - "AI Active" status */}
                    <div className="flex items-center gap-3 mb-3 pr-2 relative z-10">
                        <div className="relative flex h-3.5 w-3.5">
                            {isMarketOpen ? (
                                <>
                                    <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-green-500"></span>
                                    <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-green-400 border-2 border-[#0F172A]"></span>
                                </>
                            ) : (
                                <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-gray-500 border-2 border-[#0F172A]"></span>
                            )}
                        </div>
                        <h3 className="text-white font-bold text-lg tracking-wide">
                            {isMarketOpen ? t('marketStatusLive') : (status === 'holiday' ? t('marketStatusHoliday') : t('marketStatusResting'))}
                        </h3>
                    </div>

                    {/* Body Description */}
                    <p className="text-gray-300 text-sm mb-5 leading-relaxed relative z-10 font-medium font-arabic">
                        {isMarketOpen
                            ? <span dangerouslySetInnerHTML={{ __html: t('marketConnectedText') }} />
                            : (status === 'holiday' ? <span dangerouslySetInnerHTML={{ __html: t('marketClosedHolidayText', { reason: holidayReason || t('holidayReasonDefault') }) }} /> : <span>{t('marketClosedRestingText')}</span>)}
                    </p>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2.5 relative z-10">
                        <a
                            href="https://t.me/Tad_cs_BOT"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white py-2.5 px-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-emerald-500/30 text-sm"
                        >
                            🤖 {t('aiWidgetReqRec')}
                        </a>
                        <a
                            href="https://t.me/TAD_VIP_bot"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white py-2.5 px-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-purple-500/30 text-sm"
                        >
                            💎 {t('aiWidgetSubVip')}
                        </a>
                        <a
                            href="https://t.me/TAD_AI"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 py-2.5 px-4 rounded-xl font-medium transition-all text-sm"
                        >
                            📣 {t('aiWidgetFreeChan')}
                        </a>
                    </div>

                </div>
            )}

            <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                aria-expanded={open}
                aria-label={t('aiWidgetOpen')}
                title={t('aiWidgetOpen')}
                className="relative flex items-center justify-center gap-2 h-12 px-4 max-sm:w-12 max-sm:h-12 max-sm:px-0 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-bold shadow-[0_10px_30px_-10px_rgba(139,92,246,0.9)] hover:from-purple-500 hover:to-indigo-500 active:scale-95 transition-all"
            >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="7.5" width="16" height="12" rx="3.5" />
                    <path d="M12 3.5v4M9 13h.01M15 13h.01M9.5 16.5h5" />
                </svg>
                <span className="max-sm:hidden">{t('aiWidgetOpen')}</span>
                <span className={`absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#0F172A] ${isMarketOpen ? 'bg-emerald-400 pulse-dot' : 'bg-gray-400'}`} />
            </button>

        </div>
    );
};

export default AIFloatingWidget;
